import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Product } from '../types/product';
import { formatPrice } from '../utils/formatters';
import { useCart } from '../context/CartContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faStar, faChevronDown, faChevronUp, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import ProductCard from '../components/ProductCard';
import { fetchProducts, isOutOfStock } from '../utils/products';
import Breadcrumb from '../components/Breadcrumb';
import StockNotificationForm from '../components/StockNotificationForm';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [isHowToUseExpanded, setIsHowToUseExpanded] = useState(false);
  const [isImportantInfoExpanded, setIsImportantInfoExpanded] = useState(false);
  const [selectedWeight, setSelectedWeight] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleImageZoom = (image: string) => {
    setZoomedImage(image);
  };
  const closeImageZoom = () => {
    setZoomedImage(null);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [id, product]);

  const getImageSrc = (imagePath: string | undefined) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    try {
      return require(`../assets/images/${imagePath}`);
    } catch {
      return imagePath;
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const products = await fetchProducts();
        const foundProduct = products.find(p => p.id === id);

        if (foundProduct) {
          setProduct(foundProduct);
          setSelectedImage(getImageSrc(foundProduct.images?.[0]));

          if (foundProduct.metadata?.weight || foundProduct.weight) {
            setSelectedWeight(foundProduct.metadata?.weight || foundProduct.weight);
          }

          const otherProducts = products.filter(p =>
            p.id !== id &&
            p.category?.toLowerCase() === foundProduct.category?.toLowerCase()
          );
          setRecommendedProducts(otherProducts);
        } else {
          setError('Product not found');
        }
      } catch (error) {
        console.error('Error loading product:', error);
        setError('Failed to load product. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [id]);



  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(e.target.value, 10);
    setQuantity(isNaN(newQuantity) ? 1 : newQuantity);
  };

  const adjustQuantity = (amount: number) => {
    setQuantity(prevQuantity => Math.max(1, prevQuantity + amount));
  };

  const handleImageChange = (image: string) => {
    setSelectedImage(image);
  };

  const handleAddToCart = () => {
    if (product && !isOutOfStock(product)) {
      const productWithWeight = {
        ...product,
        weight: selectedWeight || product.metadata?.weight || product.weight
      };
      addToCart(productWithWeight, quantity);
    }
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [recommendedProducts]);

  if (isLoading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <Link
            to="/products"
            className="bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="pt-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {product && (
          <Breadcrumb
            items={[
              { label: 'Home', link: '/' },
              { label: 'Products', link: '/products' },
              { label: product.name }
            ]}
          />
        )}

        <div className="grid md:grid-cols-2 gap-12 p-4">
          <div>
            <div
              className="mb-6 flex items-center justify-center overflow-hidden bg-gray-100 rounded-lg cursor-zoom-in w-full h-[500px]"
              onClick={() => selectedImage && handleImageZoom(selectedImage)}
            >
              {selectedImage ? (
                <img
                  src={selectedImage}
                  alt={product.name}
                  className="w-full h-full object-contain z-10 relative"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-400">
                  <svg
                    className="w-24 h-24 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p>No image available</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-4 overflow-x-auto">
              {[...(product.images || []), ...(product.additionalImages || [])].length > 0 ? (
                [...(product.images || []), ...(product.additionalImages || [])].map((image, index) => (
                  <div
                    key={index}
                    className="aspect-square flex items-center justify-center bg-gray-100 rounded-lg cursor-pointer hover:border-2 hover:border-[#2b603e] transition-all duration-300"
                    onClick={() => handleImageChange(getImageSrc(image))}
                  >
                    <img
                      src={getImageSrc(image)}
                      alt={`${product.name} view ${index + 1}`}
                      className={`max-w-full max-h-full object-contain 
                        ${selectedImage === getImageSrc(image) ? 'border-2 border-[#2b603e]' : ''}`}
                    />
                  </div>
                ))
              ) : (
                [...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-gray-400"
                  >
                    <svg
                      className="w-12 h-12"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                ))
              )}
            </div>
          </div>

          <div>
            <div className="mb-4 flex w-full items-center justify-between">
              <h1 className="text-4xl font-bold text-gray-800 text-left">{product.name}</h1>
              {product.badge && (
                <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {product.badge}
                </span>
              )}
            </div>

            <div className="flex items-center mb-4">
              <div className="flex text-[#2b603e] mr-2">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    className={i < Math.floor(product.rating) ? 'text-[#2b603e]' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="text-gray-600 ml-2">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 text-left">Product Description:</h3>
            <p className="text-gray-600 mb-6 text-left">
              {product.shortDescription || product.description || 'Premium quality product'}
            </p>

            <div className="mb-6">
              <div className="flex items-center space-x-4 mb-4">
                <span className="text-4xl font-bold text-gray-800">
                  {formatPrice(product.price)}
                </span>

                {product.originalPrice && product.originalPrice > product.price && (
                  <span className="text-gray-500 line-through">
                    {formatPrice(product.originalPrice)}
                  </span>
                )}

                {isOutOfStock(product) && (
                  <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Out of Stock
                  </span>
                )}
              </div>

              {(product.metadata?.weight || product.weight) && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2 text-left">
                    Weight:
                  </label>
                  <div className="flex space-x-2">
                    <button
                      className={`px-4 py-2 rounded-lg border transition-all duration-300 ${selectedWeight === (product.metadata?.weight || product.weight)
                        ? 'bg-[#2b603e] text-white border-[#2b603e]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#2b603e]'
                        }`}
                      onClick={() => setSelectedWeight(product.metadata?.weight || product.weight || null)}
                    >
                      {product.metadata?.weight || product.weight}
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2 text-left">
                  Quantity:
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => adjustQuantity(-1)}
                    disabled={isOutOfStock(product)}
                    className={`px-3 py-2 rounded-l-lg transition-colors ${isOutOfStock(product)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={handleQuantityChange}
                    disabled={isOutOfStock(product)}
                    className={`w-16 text-center border border-gray-300 py-2 focus:outline-none focus:ring-2 focus:ring-[#2b603e]/20 ${isOutOfStock(product) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                      }`}
                  />
                  <button
                    onClick={() => adjustQuantity(1)}
                    disabled={isOutOfStock(product)}
                    className={`px-3 py-2 rounded-r-lg transition-colors ${isOutOfStock(product)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                  >
                    <FontAwesomeIcon icon={faChevronUp} />
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock(product)}
                className={`w-full py-4 rounded-lg transition-colors duration-300 flex items-center justify-center space-x-2 font-bold ${isOutOfStock(product)
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-[#2b603e] text-white hover:bg-[#1a3d25]'
                  }`}
              >
                <FontAwesomeIcon icon={faShoppingCart} />
                <span>{isOutOfStock(product) ? 'Out of Stock' : 'Add to Cart'}</span>
              </button>

              {/* Bulk Discount Badge */}
              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <span className="text-green-700 font-semibold text-sm">
                  🎉 Buy 3+ items, get 17% off!
                </span>
              </div>
            </div>

            {isOutOfStock(product) && (
              <StockNotificationForm
                productId={product.id}
                productName={product.name}
                variant="detail"
              />
            )}

            <div className="flex flex-col space-y-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-auto mb-2">
                    <img
                      src={require('../assets/images/lab-tested.png')}
                      alt="Lab Tested"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    Rigorously Tested in Certified Laboratories
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-24 h-auto mb-2">
                    <img
                      src={require('../assets/images/non-gmo.png')}
                      alt="Non-GMO Certified"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    100% Non-Genetically Modified Organisms
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-24 h-auto mb-2">
                    <img
                      src={require('../assets/images/vegan.png')}
                      alt="100% Vegan Certified"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    Completely Plant-Based Ingredients
                  </p>
                </div>
              </div>

              {/* Second Row with Eurofin Logo */}
              <div className="flex items-center justify-center w-full bg-gray-100 rounded-lg p-4">
                {/* Eurofin Logo */}
                <div className="flex flex-col items-center">
                  <div className="w-48 h-auto mb-2">
                    <img
                      src={require('../assets/images/eurofin.png')}
                      alt="Eurofin Certified"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-800 font-semibold text-center">
                    Certified Testing Facility
                  </p>
                  <p className="text-xs text-gray-600 text-center mt-1">
                    Comprehensive Quality Assurance by Eurofin Laboratories
                  </p>
                </div>
              </div>
            </div>

            {/* Usage Instructions */}
            {product.usage && product.usage !== 'Follow recommended dosage' && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <p className="text-gray-600">{product.usage}</p>
              </div>
            )}

            {/* How to Use Section */}
            {(product.metadata?.how_to_use || product.how_to_use) && (
              <div className="mb-4 border-t border-gray-200 pt-4">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setIsHowToUseExpanded(!isHowToUseExpanded)}
                >
                  <h3 className="text-xl font-semibold text-gray-800">How to Use</h3>
                  <FontAwesomeIcon
                    icon={isHowToUseExpanded ? faChevronUp : faChevronDown}
                    className="text-gray-600"
                  />
                </div>
                {isHowToUseExpanded && (
                  <div className="text-gray-600 mt-2 space-y-2 text-left">
                    {(() => {
                      try {
                        const howToUseData = product.metadata?.how_to_use || product.how_to_use;
                        const instructions = JSON.parse(howToUseData!.replace(/'/g, '"'));
                        return instructions.map((instruction: string, index: number) => (
                          <p key={index} className="flex items-start">
                            <span className="mr-2 text-[#2b603e]">•</span>
                            <span>{instruction}</span>
                          </p>
                        ));
                      } catch (error) {
                        console.error('Error parsing how_to_use:', error);
                        return null;
                      }
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Important Information Section */}
            {(product.metadata?.important_info || product.important_info) && (
              <div className="mb-4 border-t border-gray-200 pt-4">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setIsImportantInfoExpanded(!isImportantInfoExpanded)}
                >
                  <h3 className="text-xl font-semibold text-gray-800">Important Information</h3>
                  <FontAwesomeIcon
                    icon={isImportantInfoExpanded ? faChevronUp : faChevronDown}
                    className="text-gray-600"
                  />
                </div>
                {isImportantInfoExpanded && (
                  <div className="text-gray-600 mt-2 space-y-2 text-left">
                    {(() => {
                      try {
                        const importantInfoData = product.metadata?.important_info || product.important_info;
                        const infoItems = JSON.parse(importantInfoData!.replace(/'/g, '"'));
                        return infoItems.map((info: string, index: number) => (
                          <p key={index} className="flex items-start">
                            <span className="mr-2 text-[#2b603e]">•</span>
                            <span>{info}</span>
                          </p>
                        ));
                      } catch (error) {
                        console.error('Error parsing important_info:', error);
                        return null;
                      }
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Frequently Bought Together Section */}
      {product && recommendedProducts.length > 0 && (
        <section className="border-t border-gray-200 py-12">
          <div className="container mx-auto px-4">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-left bg-clip-text text-transparent bg-gradient-to-r from-[#2b603e] to-[#1a3d25]">
                Frequently Bought Together
              </h2>
              <div className="border-b-2 border-[#2b603e] w-20 mt-2"></div>
            </div>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              {(() => {
                const compatibleProducts = recommendedProducts
                  .filter(p =>
                    p.category === product.category &&
                    p.id !== product.id &&
                    p.price < product.price * 1.2 &&
                    p.price > product.price * 0.8
                  )
                  .sort((a, b) => Math.abs(a.price - product.price) - Math.abs(b.price - product.price))
                  .slice(0, 1);

                const bundleProduct = compatibleProducts[0];

                return (
                  <>
                    {/* Current Product */}
                    <div className="flex flex-col items-start w-full lg:w-80 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                      <div className="w-full h-56 mb-2 overflow-hidden flex items-center justify-center">
                        <img
                          src={getImageSrc(product.images?.[0])}
                          alt={product.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <h3 className="text-xl font-medium text-gray-700 w-full min-h-[3rem] break-words text-left">
                        <span className="text-xl text-gray-700 font-bold mr-2">This item:</span>
                        {product.name}
                      </h3>
                      <p className="text-2xl text-[#2b603e] font-bold">
                        {formatPrice(product.price)}
                      </p>
                    </div>

                    {/* Plus icon for narrow screens */}
                    {bundleProduct && (
                      <div className="flex justify-center lg:hidden">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </div>
                    )}

                    {/* Frequently Bought Product */}
                    {bundleProduct && (
                      <>
                        <div className="hidden lg:flex items-center justify-center w-12 self-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-8 w-8 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </div>

                        <div className="flex flex-col items-start w-full lg:w-80 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                          <div className="w-full h-56 mb-2 overflow-hidden flex items-center justify-center">
                            <img
                              src={getImageSrc(bundleProduct.images?.[0])}
                              alt={bundleProduct.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <h3 className="text-xl font-medium text-gray-700 w-full min-h-[3rem] break-words text-left">
                            <Link
                              to={`/product/${bundleProduct.id}`}
                              className="hover:text-[#2b603e] hover:underline transition-colors"
                            >
                              {bundleProduct.name}
                            </Link>
                          </h3>
                          <p className="text-2xl text-[#2b603e] font-bold">
                            {formatPrice(bundleProduct.price)}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Add to Cart Section */}
                    <div className="w-full lg:w-64 bg-white rounded-xl shadow-sm border border-gray-100 p-4 self-start">
                      <div className="text-xl lg:text-2xl font-semibold mb-4 text-gray-800">
                        Total Price: {formatPrice(product.price + (bundleProduct?.price || 0))}
                      </div>
                      <button
                        onClick={() => {
                          if (!isOutOfStock(product)) {
                            addToCart(product, quantity);
                            compatibleProducts.forEach(p => {
                              if (!isOutOfStock(p)) {
                                addToCart(p, 1);
                              }
                            });
                          }
                        }}
                        disabled={isOutOfStock(product)}
                        className={`w-full lg:w-auto px-6 py-3 rounded-lg transition-colors ${isOutOfStock(product)
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-[#2b603e] text-white hover:bg-[#1a3d25]'
                          }`}
                      >
                        {isOutOfStock(product) ? 'Out of Stock' : 'Add Both to Cart'}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Recommended Products Section */}
      {recommendedProducts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-left bg-clip-text text-transparent bg-gradient-to-r from-[#2b603e] to-[#1a3d25]">
                You May Also Like
              </h2>
              <div className="border-b-2 border-[#2b603e] w-20 mt-2"></div>
            </div>
            <div className="relative">
              {/* Left Arrow */}
              {canScrollLeft && (
                <button
                  onClick={scrollLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-3 shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Scroll left"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-[#2b603e] text-lg" />
                </button>
              )}

              {/* Right Arrow */}
              {canScrollRight && (
                <button
                  onClick={scrollRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-3 shadow-lg hover:bg-gray-50 transition-colors"
                  aria-label="Scroll right"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-[#2b603e] text-lg" />
                </button>
              )}

              <div
                ref={scrollContainerRef}
                className="flex items-start justify-start space-x-6 overflow-x-auto pb-4 scrollbar-hide px-12"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onScroll={checkScrollButtons}
              >
                {recommendedProducts.map(recommendedProduct => (
                  <div key={recommendedProduct.id} className="flex-shrink-0 w-80">
                    <div className="w-full h-62 mb-2 overflow-hidden flex items-center justify-center">
                      <img
                        src={getImageSrc(recommendedProduct.images?.[0])}
                        alt={recommendedProduct.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-medium text-gray-700 w-full min-h-[3rem] break-words text-left">
                      <Link
                        to={`/product/${recommendedProduct.id}`}
                        className="hover:text-[#2b603e] hover:underline transition-colors"
                      >
                        {recommendedProduct.name}
                      </Link>
                    </h3>
                    <div className="flex items-center mt-2">
                      <div className="flex text-[#2b603e] mr-2">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon
                            key={i}
                            icon={faStar}
                            className={i < Math.floor(recommendedProduct.rating) ? 'text-[#2b603e]' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <span className="text-gray-600 text-sm">
                        ({recommendedProduct.reviews} reviews)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl text-[#2b603e] font-bold">
                        {formatPrice(recommendedProduct.price)}
                      </span>
                      {recommendedProduct.originalPrice && recommendedProduct.originalPrice > recommendedProduct.price && (
                        <span className="text-gray-500 line-through text-sm">
                          {formatPrice(recommendedProduct.originalPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
          onClick={closeImageZoom}
        >
          <div
            className="max-w-[90%] max-h-[90%] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImageZoom}
              className="absolute top-0 right-0 m-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/75 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={zoomedImage}
              alt="Zoomed product"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;